import React, { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";
import axios from "axios";

import {
  Bell,
  CalendarDays,
  Search,
  ArrowUpRight,
  FileText,
  Sparkles,
  Clock,
  X,
  Megaphone,
  ChevronRight,
  RefreshCw,
} from "lucide-react";

import LogoFallback from "/img/logo.png";

export default function AllNotice({
  logoSrc = LogoFallback,
  accent = "#014864",
  pageTitle = "Notice | University of Creative Technology Chittagong",
}) {
  const [notices, setNotices] = useState([]);
  const [searchDate, setSearchDate] = useState("");
  const [searchTitle, setSearchTitle] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // --------------------------------------------------
  // Fetch Notices
  // --------------------------------------------------

  const handlePost = async () => {
    setLoading(true);
    setError(null);

    try {
      const payload = {
        userId: import.meta.env.VITE_API_USER,
        password: import.meta.env.VITE_API_PASS,
      };

      const api = axios.create({
        baseURL: import.meta.env.VITE_API_BASE_URL,
        headers: {
          "Content-Type": "application/json",
        },
      });

      const response = await api.post("/all-notice", payload);

      const data = Array.isArray(response.data)
        ? response.data
        : [];

      setNotices(data);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          "Unable to load notices."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handlePost();
  }, []);

  // --------------------------------------------------
  // Browser Title
  // --------------------------------------------------

  useEffect(() => {
    document.title = pageTitle;
  }, [pageTitle]);

  // --------------------------------------------------
  // Filter Notices
  // --------------------------------------------------

  const filteredNotices = useMemo(() => {
    return notices
      .filter((notice) => {
        const title = String(notice.head_line || "").toLowerCase();

        const date = String(notice.date || "").slice(0, 10);

        const matchesTitle = title.includes(
          searchTitle.trim().toLowerCase()
        );

        const matchesDate = searchDate
          ? date === searchDate
          : true;

        return matchesTitle && matchesDate;
      })
      .slice(0, 50);
  }, [notices, searchTitle, searchDate]);

  // --------------------------------------------------
  // Date Formatter
  // --------------------------------------------------

  const formatDate = (dateString) => {
    if (!dateString) return "Date unavailable";

    const date = new Date(dateString);

    if (Number.isNaN(date.getTime())) {
      return dateString;
    }

    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  };

  const getDay = (dateString) => {
    if (!dateString) return "--";

    const date = new Date(dateString);

    if (Number.isNaN(date.getTime())) {
      return "--";
    }

    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
    });
  };

  const getMonth = (dateString) => {
    if (!dateString) return "---";

    const date = new Date(dateString);

    if (Number.isNaN(date.getTime())) {
      return "---";
    }

    return date
      .toLocaleDateString("en-GB", {
        month: "short",
      })
      .toUpperCase();
  };

  // --------------------------------------------------
  // Animation Variants
  // --------------------------------------------------

  const containerVariants = {
    hidden: {
      opacity: 0,
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
      },
    },
  };

  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 30,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut",
      },
    },
  };

  // --------------------------------------------------
  // Render
  // --------------------------------------------------

  return (
    <div
      className="min-h-screen bg-slate-50 overflow-hidden"
      style={{
        "--accent": accent,
      }}
    >
      {/* ================================================
          HERO SECTION
      ================================================ */}

      <section className="relative bg-[#014864] text-white overflow-hidden">

        {/* Background Gradient */}

        <div className="absolute inset-0 bg-gradient-to-br from-[#014864] via-[#036987] to-[#0c9db0]" />

        {/* Animated Background Circles */}

        <motion.div
          animate={{
            x: [0, 40, 0],
            y: [0, -30, 0],
            scale: [1, 1.1, 1],
          }}
          transition={{
            duration: 12,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-cyan-300/10 blur-3xl"
        />

        <motion.div
          animate={{
            x: [0, -30, 0],
            y: [0, 30, 0],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className="absolute -bottom-40 -left-40 w-[450px] h-[450px] rounded-full bg-teal-300/10 blur-3xl"
        />

        {/* Decorative Grid */}

        <div
          className="absolute inset-0 opacity-[0.08]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.4) 1px, transparent 1px)",
            backgroundSize: "40px 40px",
          }}
        />

        <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-10 lg:px-20 py-20 md:py-28">

          <div className="grid lg:grid-cols-2 gap-12 items-center">

            {/* Hero Text */}

            <motion.div
              initial={{
                opacity: 0,
                x: -50,
              }}
              animate={{
                opacity: 1,
                x: 0,
              }}
              transition={{
                duration: 0.8,
              }}
            >

              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 backdrop-blur-md mb-6">

                <Sparkles
                  size={16}
                  className="text-yellow-300"
                />

                <span className="text-sm font-medium tracking-wide">
                  Official University Announcements
                </span>

              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight">

                UCTC

                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-cyan-200 to-teal-200">
                  Notice Board
                </span>

              </h1>

              <p className="mt-6 text-white/75 text-base md:text-lg leading-relaxed max-w-xl">
                Stay informed with the latest announcements,
                academic updates, examination schedules, and
                important university notifications.
              </p>

              {/* Statistics */}

              <div className="flex flex-wrap gap-4 mt-8">

                <div className="flex items-center gap-3 bg-white/10 border border-white/10 rounded-2xl px-5 py-4 backdrop-blur-md">

                  <div className="w-11 h-11 rounded-xl bg-cyan-300/20 flex items-center justify-center">
                    <Bell
                      size={22}
                      className="text-cyan-200"
                    />
                  </div>

                  <div>
                    <p className="text-2xl font-bold">
                      {notices.length}
                    </p>

                    <p className="text-xs text-white/60">
                      Total Notices
                    </p>
                  </div>

                </div>

                <div className="flex items-center gap-3 bg-white/10 border border-white/10 rounded-2xl px-5 py-4 backdrop-blur-md">

                  <div className="w-11 h-11 rounded-xl bg-teal-300/20 flex items-center justify-center">
                    <CalendarDays
                      size={22}
                      className="text-teal-200"
                    />
                  </div>

                  <div>
                    <p className="text-2xl font-bold">
                      {filteredNotices.length}
                    </p>

                    <p className="text-xs text-white/60">
                      Matching Notices
                    </p>
                  </div>

                </div>

              </div>

            </motion.div>

            {/* Hero Illustration */}

            <motion.div
              initial={{
                opacity: 0,
                scale: 0.8,
                rotate: -5,
              }}
              animate={{
                opacity: 1,
                scale: 1,
                rotate: 0,
              }}
              transition={{
                duration: 0.8,
                delay: 0.2,
              }}
              className="relative hidden md:flex justify-center items-center"
            >

              <motion.div
                animate={{
                  y: [0, -12, 0],
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className="relative w-80 h-80 lg:w-[400px] lg:h-[400px]"
              >

                <div className="absolute inset-0 rounded-full border border-white/20" />

                <div className="absolute inset-8 rounded-full border border-white/10" />

                <div className="absolute inset-16 rounded-full bg-white/5 backdrop-blur-sm" />

                <div className="absolute inset-0 flex items-center justify-center">

                  <div className="w-48 h-60 bg-white rounded-3xl shadow-2xl p-5 rotate-[-5deg]">

                    <div className="w-12 h-12 rounded-2xl bg-[#014864] flex items-center justify-center mb-5">
                      <Megaphone
                        size={25}
                        className="text-white"
                      />
                    </div>

                    <div className="space-y-3">

                      <div className="h-3 w-28 rounded-full bg-[#014864]" />

                      <div className="h-2 w-full rounded-full bg-slate-200" />

                      <div className="h-2 w-4/5 rounded-full bg-slate-200" />

                      <div className="h-2 w-full rounded-full bg-slate-200" />

                      <div className="h-2 w-3/5 rounded-full bg-slate-200" />

                    </div>

                    <div className="mt-7 flex items-center gap-2">

                      <div className="w-8 h-8 rounded-lg bg-cyan-100" />

                      <div className="flex-1 space-y-2">
                        <div className="h-2 w-full rounded-full bg-slate-200" />
                        <div className="h-2 w-2/3 rounded-full bg-slate-200" />
                      </div>

                    </div>

                  </div>

                </div>

                {/* Floating Icons */}

                <motion.div
                  animate={{
                    y: [0, -10, 0],
                    rotate: [0, 5, 0],
                  }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                  }}
                  className="absolute top-12 right-0 w-14 h-14 rounded-2xl bg-white shadow-xl flex items-center justify-center"
                >
                  <Bell
                    size={25}
                    className="text-[#014864]"
                  />
                </motion.div>

                <motion.div
                  animate={{
                    y: [0, 10, 0],
                    rotate: [0, -5, 0],
                  }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                  }}
                  className="absolute bottom-12 left-0 w-14 h-14 rounded-2xl bg-cyan-300 shadow-xl flex items-center justify-center"
                >
                  <FileText
                    size={25}
                    className="text-[#014864]"
                  />
                </motion.div>

              </motion.div>

            </motion.div>

          </div>

        </div>

        {/* Bottom Wave */}

        <div className="relative -mb-1">
          <svg
            viewBox="0 0 1440 100"
            className="w-full h-16 md:h-24"
            preserveAspectRatio="none"
          >
            <path
              d="M0,50 C240,100 480,0 720,50 C960,100 1200,0 1440,50 L1440,100 L0,100 Z"
              fill="#f8fafc"
            />
          </svg>
        </div>

      </section>

      {/* ================================================
          NOTICE SECTION
      ================================================ */}

      <section className="relative py-16 md:py-20 px-4 sm:px-8 lg:px-16">

        <div className="max-w-6xl mx-auto">

          {/* Section Heading */}

          <motion.div
            initial={{
              opacity: 0,
              y: 25,
            }}
            whileInView={{
              opacity: 1,
              y: 0,
            }}
            viewport={{
              once: true,
            }}
            transition={{
              duration: 0.6,
            }}
            className="text-center mb-12"
          >

            <div className="inline-flex items-center gap-2 text-[#014864] font-semibold text-sm uppercase tracking-widest mb-3">

              <span className="w-8 h-[2px] bg-[#014864]" />

              University Updates

              <span className="w-8 h-[2px] bg-[#014864]" />

            </div>

            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-800">
              Latest <span className="text-[#014864]">Notices</span>
            </h2>

            <p className="mt-4 text-slate-500 max-w-xl mx-auto">
              Browse the latest official notices published by
              the University of Creative Technology Chittagong.
            </p>

          </motion.div>

          {/* Search Panel */}

          <motion.div
            initial={{
              opacity: 0,
              y: 30,
            }}
            whileInView={{
              opacity: 1,
              y: 0,
            }}
            viewport={{
              once: true,
            }}
            transition={{
              duration: 0.6,
            }}
            className="relative bg-white rounded-3xl shadow-lg shadow-slate-200/60 border border-slate-100 p-5 md:p-7 mb-10"
          >

            <div className="flex flex-col md:flex-row gap-4">

              {/* Date Search */}

              <div className="relative flex-1">

                <CalendarDays
                  size={20}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-[#014864]"
                />

                <input
                  type="date"
                  value={searchDate}
                  onChange={(e) =>
                    setSearchDate(e.target.value)
                  }
                  className="w-full pl-12 pr-4 py-4 rounded-2xl bg-slate-50 border border-slate-200 text-slate-700 outline-none transition-all focus:bg-white focus:border-[#014864] focus:ring-4 focus:ring-[#014864]/10"
                />

              </div>

              {/* Title Search */}

              <div className="relative flex-[2]">

                <Search
                  size={20}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-[#014864]"
                />

                <input
                  type="text"
                  placeholder="Search notices by title..."
                  value={searchTitle}
                  onChange={(e) =>
                    setSearchTitle(e.target.value)
                  }
                  className="w-full pl-12 pr-12 py-4 rounded-2xl bg-slate-50 border border-slate-200 text-slate-700 outline-none transition-all focus:bg-white focus:border-[#014864] focus:ring-4 focus:ring-[#014864]/10"
                />

                {searchTitle && (
                  <button
                    type="button"
                    onClick={() => setSearchTitle("")}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-red-500"
                  >
                    <X size={20} />
                  </button>
                )}

              </div>

              {/* Clear Filters */}

              {(searchDate || searchTitle) && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchDate("");
                    setSearchTitle("");
                  }}
                  className="px-5 py-3 rounded-2xl bg-red-50 text-red-600 font-semibold hover:bg-red-100 transition"
                >
                  Clear
                </button>
              )}

            </div>

            <div className="flex items-center justify-between mt-5 text-sm text-slate-500">

              <div className="flex items-center gap-2">
                <Search size={15} />

                <span>
                  {filteredNotices.length} notice
                  {filteredNotices.length !== 1 ? "s" : ""} found
                </span>
              </div>

              <span className="hidden sm:flex items-center gap-2">
                <Clock size={15} />
                Latest announcements
              </span>

            </div>

          </motion.div>

          {/* ============================================
              LOADING SKELETON
          ============================================ */}

          {loading && (
            <div className="space-y-5">

              {[1, 2, 3, 4].map((item) => (
                <div
                  key={item}
                  className="animate-pulse bg-white rounded-3xl border border-slate-100 p-5 flex gap-5"
                >

                  <div className="w-20 h-20 bg-slate-200 rounded-2xl shrink-0" />

                  <div className="flex-1 space-y-4 py-2">
                    <div className="h-4 w-1/3 bg-slate-200 rounded-full" />
                    <div className="h-4 w-3/4 bg-slate-200 rounded-full" />
                  </div>

                </div>
              ))}

            </div>
          )}

          {/* ============================================
              ERROR STATE
          ============================================ */}

          {!loading && error && (
            <motion.div
              initial={{
                opacity: 0,
                scale: 0.95,
              }}
              animate={{
                opacity: 1,
                scale: 1,
              }}
              className="bg-red-50 border border-red-100 rounded-3xl p-10 text-center"
            >

              <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-5">
                <RefreshCw
                  size={28}
                  className="text-red-500"
                />
              </div>

              <h3 className="text-xl font-bold text-slate-800">
                Unable to Load Notices
              </h3>

              <p className="mt-3 text-slate-500">
                {error}
              </p>

              <button
                onClick={handlePost}
                className="mt-6 inline-flex items-center gap-2 bg-[#014864] text-white px-6 py-3 rounded-xl hover:bg-[#036987] transition"
              >
                <RefreshCw size={18} />
                Try Again
              </button>

            </motion.div>
          )}

          {/* ============================================
              NOTICE CARDS
          ============================================ */}

          {!loading && !error && filteredNotices.length > 0 && (

            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="space-y-5"
            >

              <AnimatePresence mode="popLayout">

                {filteredNotices.map((notice, index) => (

                  <motion.div
                    key={notice.id ?? index}
                    variants={itemVariants}
                    layout
                    initial={{
                      opacity: 0,
                      y: 25,
                    }}
                    animate={{
                      opacity: 1,
                      y: 0,
                    }}
                    exit={{
                      opacity: 0,
                      scale: 0.95,
                    }}
                    transition={{
                      duration: 0.4,
                    }}
                    whileHover={{
                      y: -4,
                    }}
                    className="group relative"
                  >

                    <div className="relative bg-white border border-slate-100 rounded-3xl p-5 md:p-6 shadow-sm hover:shadow-xl hover:shadow-[#014864]/10 hover:border-[#014864]/20 transition-all duration-300">

                      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">

                        {/* Date Badge */}

                        <div className="shrink-0 flex sm:flex-col items-center justify-center gap-3 sm:gap-0 bg-gradient-to-br from-[#014864] to-[#0785a3] text-white rounded-2xl w-full sm:w-20 h-auto sm:h-20 py-3 sm:py-0 shadow-lg shadow-[#014864]/20">

                          <span className="text-2xl sm:text-3xl font-extrabold leading-none">
                            {getDay(notice.date)}
                          </span>

                          <span className="text-sm sm:text-xs font-semibold tracking-widest sm:mt-1">
                            {getMonth(notice.date)}
                          </span>

                        </div>

                        {/* Notice Content */}

                        <div className="flex-1 min-w-0">

                          <div className="flex flex-wrap items-center gap-2 mb-3">

                            <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-[#014864] bg-cyan-50 px-3 py-1.5 rounded-full">

                              <FileText size={13} />

                              Official Notice

                            </span>

                            <span className="text-xs text-slate-400">
                              {formatDate(notice.date)}
                            </span>

                          </div>

                          <h3 className="text-base md:text-lg font-bold text-slate-800 leading-relaxed group-hover:text-[#014864] transition-colors">

                            {notice.head_line}

                          </h3>

                        </div>

                        {/* Details Button */}

                        <div className="w-full sm:w-auto shrink-0">

                          <Link
                            to={`/notice/${notice.id}`}
                            className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-6 py-3.5 rounded-2xl bg-slate-50 text-[#014864] font-bold text-sm border border-slate-200 group-hover:bg-[#014864] group-hover:text-white group-hover:border-[#014864] transition-all duration-300"
                          >

                            View Details

                            <ArrowUpRight
                              size={18}
                              className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1"
                            />

                          </Link>

                        </div>

                      </div>

                      {/* Decorative Accent */}

                      <div className="absolute left-0 top-6 bottom-6 w-1 rounded-r-full bg-[#014864] opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                    </div>

                  </motion.div>

                ))}

              </AnimatePresence>

            </motion.div>

          )}

          {/* ============================================
              EMPTY STATE
          ============================================ */}

          {!loading && !error && filteredNotices.length === 0 && (

            <motion.div
              initial={{
                opacity: 0,
                y: 30,
              }}
              animate={{
                opacity: 1,
                y: 0,
              }}
              className="bg-white border border-slate-100 rounded-3xl p-12 md:p-16 text-center shadow-sm"
            >

              <motion.div
                animate={{
                  y: [0, -8, 0],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                }}
                className="w-24 h-24 rounded-3xl bg-cyan-50 flex items-center justify-center mx-auto mb-6"
              >

                <Bell
                  size={42}
                  className="text-[#014864]"
                />

              </motion.div>

              <h3 className="text-2xl font-bold text-slate-800">
                No Notices Found
              </h3>

              <p className="mt-3 text-slate-500 max-w-md mx-auto">
                We couldn't find any notices matching your
                search. Try another date or a different keyword.
              </p>

              {(searchDate || searchTitle) && (
                <button
                  onClick={() => {
                    setSearchDate("");
                    setSearchTitle("");
                  }}
                  className="mt-7 px-6 py-3 rounded-xl bg-[#014864] text-white font-semibold hover:bg-[#036987] transition"
                >
                  Clear Search
                </button>
              )}

            </motion.div>

          )}

          {/* Footer */}

          {!loading && !error && notices.length > 0 && (

            <motion.div
              initial={{
                opacity: 0,
              }}
              whileInView={{
                opacity: 1,
              }}
              viewport={{
                once: true,
              }}
              className="text-center mt-12"
            >

              <div className="inline-flex items-center gap-3 bg-white border border-slate-100 shadow-sm rounded-full px-6 py-3">

                <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />

                <span className="text-sm text-slate-500">
                  You are viewing the latest{" "}
                  {filteredNotices.length} notices
                </span>

              </div>

            </motion.div>

          )}

        </div>

      </section>

    </div>
  );
}

AllNotice.propTypes = {
  logoSrc: PropTypes.string,
  accent: PropTypes.string,
  pageTitle: PropTypes.string,
};